import React, { useEffect, useRef, useState } from 'react';
import { Server, Zap, Snowflake, Globe, Play, RotateCcw } from 'lucide-react';

interface StartScreenProps {
  hasSave: boolean;
  onContinue: () => void;
  onNewGame: () => void;
  saveSummary?: string; // e.g. "Day 12 · $438,200" — shown under Continue
}

// Full-screen title / welcome screen shown when the game page loads. Sets the
// mental model (build bottom-up: power -> cooling -> servers -> internet) before
// the player is dropped into the sim, and gates the game behind a Play button.
const StartScreen: React.FC<StartScreenProps> = ({ hasSave, onContinue, onNewGame, saveSummary }) => {
  const [mounted, setMounted] = useState(false);
  const primaryRef = useRef<HTMLButtonElement>(null);

  const primaryAction = hasSave ? onContinue : onNewGame;

  useEffect(() => {
    setMounted(true);
    // Autofocus the primary button so Enter/Space works immediately.
    const t = setTimeout(() => primaryRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); primaryAction(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [primaryAction]);

  const steps = [
    { icon: <Zap className="w-5 h-5" />, label: 'Power', color: 'text-yellow-400', desc: 'Grid → transformer → UPS' },
    { icon: <Snowflake className="w-5 h-5" />, label: 'Cooling', color: 'text-cyan-400', desc: 'Heat rejection → chiller → CRAH' },
    { icon: <Server className="w-5 h-5" />, label: 'Servers', color: 'text-emerald-400', desc: 'Rack them, wire the network' },
    { icon: <Globe className="w-5 h-5" />, label: 'Internet', color: 'text-blue-400', desc: 'Connect — now they earn' },
  ];

  return (
    <div className="fixed inset-0 z-[200] bg-gray-950 flex items-center justify-center p-4 overflow-y-auto">
      {/* Subtle grid backdrop, echoing the in-game floor */}
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(59,130,246,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.4) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Faint animated rack-LED strip along the bottom — datacenter flavour */}
      <div className="absolute bottom-0 inset-x-0 h-16 flex items-end justify-center gap-1.5 pb-3 opacity-40 pointer-events-none overflow-hidden">
        {Array.from({ length: 48 }).map((_, i) => {
          const colors = ['bg-emerald-400', 'bg-blue-400', 'bg-emerald-400', 'bg-amber-400'];
          return (
            <span
              key={i}
              className={`w-1 rounded-sm ${colors[i % colors.length]} animate-pulse motion-reduce:animate-none`}
              style={{ height: `${6 + ((i * 37) % 22)}px`, animationDelay: `${(i % 8) * 160}ms`, animationDuration: `${1400 + (i % 5) * 220}ms` }}
            />
          );
        })}
      </div>
      <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />

      <div
        className={`relative w-full max-w-2xl text-center transition-all duration-500 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs uppercase tracking-[0.3em] text-gray-400">Browser Tycoon</span>
        </div>

        <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tight text-white">
          Datacenter <span className="text-emerald-400">Tycoon</span>
        </h1>
        <p className="mt-5 text-lg text-gray-400 max-w-xl mx-auto">
          Start with $500,000 and an empty floor. Build a datacenter from the ground up
          and grow it into an empire across Europe.
        </p>

        {/* Mental model — the one thing new players need to know */}
        <div className="mt-9 bg-gray-900/70 border border-gray-800 rounded-2xl p-5">
          <p className="text-sm text-gray-300 mb-4">
            You build <span className="text-white font-semibold">bottom-up</span>. Servers only earn
            money once they are cooled, powered, and connected to the internet.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {steps.map((s, i) => (
              <div key={s.label} className="relative bg-gray-800/60 rounded-xl p-3 flex flex-col items-center">
                <div className={`${s.color} mb-1`}>{s.icon}</div>
                <div className="text-sm font-semibold text-white">{s.label}</div>
                <div className="text-[11px] text-gray-400 mt-0.5 leading-tight">{s.desc}</div>
                {i < steps.length - 1 && (
                  <span className="hidden sm:block absolute -right-2 top-1/2 -translate-y-1/2 text-gray-600">›</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
          {hasSave ? (
            <>
              <button
                ref={primaryRef}
                onClick={onContinue}
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg shadow-lg shadow-emerald-900/40 flex flex-col items-center justify-center gap-0.5 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              >
                <span className="flex items-center gap-2"><Play className="w-5 h-5" /> Continue</span>
                {saveSummary && <span className="text-[11px] font-medium text-emerald-100/80">{saveSummary}</span>}
              </button>
              <button
                onClick={onNewGame}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-200 font-semibold flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                <RotateCcw className="w-4 h-4" /> New Game
              </button>
            </>
          ) : (
            <button
              ref={primaryRef}
              onClick={onNewGame}
              className="w-full sm:w-auto px-10 py-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xl shadow-lg shadow-emerald-900/40 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            >
              <Play className="w-6 h-6" /> Play
            </button>
          )}
        </div>

        <p className="mt-6 text-xs text-gray-600">
          A Setup Guide walks you through your first server. Press <kbd className="px-1 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-400">Enter</kbd> to begin. Progress saves automatically in your browser.
        </p>
      </div>
    </div>
  );
};

export default StartScreen;
